import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AdminController, AdminUserSummary } from '../../controllers/admin'
import IdLookup from './IdLookup'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

interface IProps {
  loading: boolean
  onSelect: (id: string) => void
}

export default function UserLookup({ loading, onSelect }: IProps) {
  const { t } = useTranslation()
  const [results, setResults] = React.useState<AdminUserSummary[] | null>(null)
  const [searching, setSearching] = React.useState(false)

  const select = (id: string) => {
    setResults(null)
    onSelect(id)
  }

  const search = async (query: string) => {
    if (UUID_REGEX.test(query)) return select(query)

    setSearching(true)
    try {
      const users = await AdminController.searchUsers(query)
      if (users.length === 1) {
        select(users[0].id)
      } else {
        setResults(users)
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setSearching(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <IdLookup
        label={t('admin::Email or user ID')}
        placeholder={t('admin::Search by email')}
        loading={loading || searching}
        onSubmit={search}
      />
      {results && results.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('admin::No users found.')}</p>
      )}
      {results && results.length > 0 && (
        <ul className="flex flex-col rounded-lg border">
          {results.map((user) => (
            <li key={user.id}>
              <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => select(user.id)}>
                <span>{user.email}</span>
                {user.name && <span className="text-muted-foreground">{user.name}</span>}
                {user.admin && <Badge variant="secondary">{t('admin::Admin')}</Badge>}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
